import {
  useMutation,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { toast } from "sonner";

import { ApiError } from "@/api/errors";

/** Sidebar badges: every queue mutation invalidates this so counts update at once. */
export const REVIEW_SUMMARY_KEY = ["reviews", "summary"] as const;

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Request failed";

/**
 * "The item is no longer actionable" — someone else handled it, or the
 * customer moved on (400 already-reviewed / stale, 404 gone). The queue
 * pages drop such rows and advance instead of leaving a dead item selected.
 */
export const isStaleItemError = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 400 || error.status === 404);

/**
 * Shared shape for queue actions: toast the (masked, generic) server error,
 * run the caller's onSuccess, and invalidate the queue keys + the summary on
 * settle. Mirrors useUserMutation in ./users.ts.
 */
export const useQueueMutation = <TInput, TOutput>(
  invalidateKeys: readonly QueryKey[],
  mutationFn: (input: TInput) => Promise<TOutput>,
  options: {
    onSuccess?: (data: TOutput, input: TInput) => void;
    /** Suppress the error toast for these (the caller explains it better). */
    quietErrors?: (error: unknown) => boolean;
  } = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: options.onSuccess,
    onError: (error) => {
      if (options.quietErrors?.(error)) return;
      toast.error(errorMessage(error));
    },
    onSettled: () => {
      for (const key of invalidateKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      void queryClient.invalidateQueries({ queryKey: REVIEW_SUMMARY_KEY });
    },
  });
};
